import React, { useEffect, useState } from 'react';
import FilterCheckboxList from '../filters/FilterCheckboxList';
import FilterIcon from '../filters/FilterIcon';
import FilterSection from '../filters/FilterSection';
import { FilterOptionCounts, withOptionCounts } from '../filters/counts';
import { FilterOption, getGroupSelectionSummary, getSelectionSummary } from '../filters/selection';

type SectionKey =
  | 'calendar'
  | 'contributingOrganisations'
  | 'search'
  | 'matauranga'
  | 'observation'
  | 'projects'
  | 'locations'
  | 'monitoring'
  | 'monitoringDomains'
  | 'monitoringGroups'
  | 'monitoringLocations';

const DEFAULT_EXPANDED: Record<SectionKey, boolean> = {
  calendar: false,
  contributingOrganisations: false,
  search: false,
  matauranga: false,
  observation: false,
  projects: false,
  locations: false,
  monitoring: false,
  monitoringDomains: false,
  monitoringGroups: false,
  monitoringLocations: false,
};

const SEARCH_DEBOUNCE_MS = 400;

export type TuhonoFilterCounts = FilterOptionCounts;

export interface TuhonoFilterPanelProps {
  searchTerm: string;
  contributingOrganisationsOptions: FilterOption[];
  selectedContributingOrganisations: string[];
  contributingProjectOptions: FilterOption[];
  selectedProjects: string[];
  locationOptions: FilterOption[];
  selectedLocations: string[];
  monitoringGroupOptions: FilterOption[];
  selectedMonitoringGroups: string[];
  monitoringLocationOptions: FilterOption[];
  selectedMonitoringLocations: string[];
  mataurangaOptions: FilterOption[];
  selectedMatauranga: string[];
  monitoringOptions: FilterOption[];
  selectedMonitoring: string[];
  mataurangaCounts: TuhonoFilterCounts;
  projectCounts: TuhonoFilterCounts;
  locationCounts: TuhonoFilterCounts;
  calendarType: string;
  calendarTypeOptions: FilterOption[];
  calendarYears: number[];
  calendarYear: number;
  calendarMonths: string[];
  calendarMonth: number;
  calendarWeeks: number[];
  calendarWeek: number;
  calendarDays: number[];
  calendarDay: number;
  onSearchTermChange: (term: string) => void;
  onContributingOrganisationsChange: (selected: string[]) => void;
  onProjectsChange: (selected: string[]) => void;
  onLocationsChange: (selected: string[]) => void;
  onMonitoringGroupsChange: (selected: string[]) => void;
  onMonitoringLocationsChange: (selected: string[]) => void;
  onMataurangaChange: (selected: string[]) => void;
  onMonitoringChange: (selected: string[]) => void;
  onCalendarTypeChange: (type: string) => void;
  onCalendarYearChange: (year: number) => void;
  onCalendarMonthChange: (month: number) => void;
  onCalendarWeekChange: (week: number) => void;
  onCalendarDayChange: (day: number) => void;
}

const TuhonoFilterPanel: React.FC<TuhonoFilterPanelProps> = ({
  searchTerm,
  contributingOrganisationsOptions,
  selectedContributingOrganisations,
  contributingProjectOptions,
  selectedProjects,
  locationOptions,
  selectedLocations,
  monitoringGroupOptions,
  selectedMonitoringGroups,
  monitoringLocationOptions,
  selectedMonitoringLocations,
  mataurangaOptions,
  selectedMatauranga,
  monitoringOptions,
  selectedMonitoring,
  mataurangaCounts,
  projectCounts,
  locationCounts,
  calendarType,
  calendarTypeOptions,
  calendarYears,
  calendarYear,
  calendarMonths,
  calendarMonth,
  calendarWeeks,
  calendarWeek,
  calendarDays,
  calendarDay,
  onSearchTermChange,
  onContributingOrganisationsChange,
  onProjectsChange,
  onLocationsChange,
  onMonitoringGroupsChange,
  onMonitoringLocationsChange,
  onMataurangaChange,
  onMonitoringChange,
  onCalendarTypeChange,
  onCalendarYearChange,
  onCalendarMonthChange,
  onCalendarWeekChange,
  onCalendarDayChange,
}) => {
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({ ...DEFAULT_EXPANDED });
  const [searchInput, setSearchInput] = useState(searchTerm);

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  // Debounce typed search so the calendar and counts do not reload on every key.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      if (next !== searchTerm) {
        onSearchTermChange(next);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput, searchTerm, onSearchTermChange]);

  const toggle = (section: SectionKey) => {
    setExpanded((current) => ({ ...current, [section]: !current[section] }));
  };

  const calendarTypeLabel = calendarTypeOptions.find((option) => option.key === calendarType)?.value || calendarType;
  const monthLabel = calendarMonths[calendarMonth] || '';
  const calendarSummary =
    calendarType === 'day'
      ? `${calendarTypeLabel} · ${calendarDay} ${monthLabel} ${calendarYear}`
      : calendarType === 'week'
      ? `${calendarTypeLabel} ${calendarWeek} · ${monthLabel} ${calendarYear}`
      : `${calendarTypeLabel} · ${monthLabel} ${calendarYear}`;

  const searchSummary = searchTerm ? `“${searchTerm}”` : 'Search';
  const projectsDisabled = selectedContributingOrganisations.length === 0;
  const observationSummary = getGroupSelectionSummary([
    { selected: selectedProjects, options: contributingProjectOptions },
    { selected: selectedLocations, options: locationOptions },
  ]);
  const monitoringSummary = getGroupSelectionSummary([
    { selected: selectedMonitoring, options: monitoringOptions },
    { selected: selectedMonitoringGroups, options: monitoringGroupOptions },
    { selected: selectedMonitoringLocations, options: monitoringLocationOptions },
  ]);

  return (
    <div className="stream-filter-panel">
      <FilterSection
        id="tuhono-calendar-view"
        title="Time / Calendar"
        icon="calendar"
        expanded={expanded.calendar}
        onToggle={() => toggle('calendar')}
        summary={calendarSummary}
        summaryIsDateRange
      >
        <div className="sf-field">
          <label className="sf-field-label" htmlFor="tuhono-calendar-type">
            View
          </label>
          <select
            id="tuhono-calendar-type"
            className="sf-select"
            value={calendarType}
            onChange={(event) => onCalendarTypeChange(event.target.value)}
          >
            {calendarTypeOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.value}
              </option>
            ))}
          </select>
        </div>

        <div className="sf-field-grid">
          <div className="sf-field">
            <label className="sf-field-label" htmlFor="tuhono-calendar-year">
              Year
            </label>
            <select
              id="tuhono-calendar-year"
              className="sf-select"
              value={calendarYear}
              onChange={(event) => onCalendarYearChange(Number(event.target.value))}
            >
              {calendarYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="sf-field">
            <label className="sf-field-label" htmlFor="tuhono-calendar-month">
              Month
            </label>
            <select
              id="tuhono-calendar-month"
              className="sf-select"
              value={calendarMonth}
              onChange={(event) => onCalendarMonthChange(Number(event.target.value))}
            >
              {calendarMonths.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
          </div>
        </div>

        {calendarType === 'week' ? (
          <div className="sf-field">
            <label className="sf-field-label" htmlFor="tuhono-calendar-week">
              Week
            </label>
            <select
              id="tuhono-calendar-week"
              className="sf-select"
              value={calendarWeek}
              onChange={(event) => onCalendarWeekChange(Number(event.target.value))}
            >
              {calendarWeeks.map((week) => (
                <option key={week} value={week}>
                  {week}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {calendarType === 'day' ? (
          <div className="sf-field">
            <label className="sf-field-label" htmlFor="tuhono-calendar-day">
              Day
            </label>
            <select
              id="tuhono-calendar-day"
              className="sf-select"
              value={calendarDay}
              onChange={(event) => onCalendarDayChange(Number(event.target.value))}
            >
              {calendarDays.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </FilterSection>

      <FilterSection
        id="tuhono-contributing-organisations"
        title="Contributing Organisation"
        icon="organisation"
        expanded={expanded.contributingOrganisations}
        onToggle={() => toggle('contributingOrganisations')}
        summary={getSelectionSummary(selectedContributingOrganisations, contributingOrganisationsOptions)}
      >
        <FilterCheckboxList
          options={contributingOrganisationsOptions}
          selected={selectedContributingOrganisations}
          onChange={onContributingOrganisationsChange}
          ariaLabel="Contributing organisations"
          emptyText="No contributing organisations available"
        />
      </FilterSection>

      <FilterSection
        id="tuhono-search"
        title="Text Search"
        icon="search"
        expanded={expanded.search}
        onToggle={() => toggle('search')}
        summary={searchSummary}
        summaryMuted={!searchTerm}
      >
        <label className="sf-field-label" htmlFor="sf-tuhono-search">
          Search observations, mātauranga and monitoring stations
        </label>
        <div className="sf-search-field">
          <span className="sf-search-field__icon" aria-hidden="true">
            <FilterIcon name="search" size={16} />
          </span>
          <input
            id="sf-tuhono-search"
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Location, notes, values…"
            autoComplete="off"
          />
        </div>
      </FilterSection>

      <FilterSection
        id="tuhono-matauranga"
        title="Mātauranga"
        icon="matauranga"
        expanded={expanded.matauranga}
        onToggle={() => toggle('matauranga')}
        summary={getSelectionSummary(selectedMatauranga, mataurangaOptions)}
      >
        <FilterCheckboxList
          options={withOptionCounts(mataurangaOptions, mataurangaCounts)}
          selected={selectedMatauranga}
          onChange={onMataurangaChange}
          ariaLabel="Mātauranga domains"
          emptyText="No mātauranga domains available"
        />
      </FilterSection>

      <FilterSection
        id="tuhono-observations"
        title="Observation"
        icon="observations"
        expanded={expanded.observation}
        onToggle={() => toggle('observation')}
        summary={observationSummary}
        className="sf-section--group"
      >
        <FilterSection
          id="tuhono-projects"
          title="Project"
          icon="project"
          expanded={expanded.projects}
          onToggle={() => toggle('projects')}
          summary={getSelectionSummary(selectedProjects, contributingProjectOptions)}
          className="sf-section--nested"
        >
          <FilterCheckboxList
            options={withOptionCounts(contributingProjectOptions, projectCounts)}
            selected={selectedProjects}
            onChange={onProjectsChange}
            ariaLabel="Projects"
            emptyText="No projects available for this site"
            disabled={projectsDisabled}
            disabledText="Select a contributing organisation to enable projects"
          />
        </FilterSection>

        <FilterSection
          id="tuhono-locations"
          title="Location"
          icon="location"
          expanded={expanded.locations}
          onToggle={() => toggle('locations')}
          summary={getSelectionSummary(selectedLocations, locationOptions)}
          className="sf-section--nested"
        >
          <FilterCheckboxList
            options={withOptionCounts(locationOptions, locationCounts)}
            selected={selectedLocations}
            onChange={onLocationsChange}
            ariaLabel="Observation locations"
            emptyText="No observation locations available"
          />
        </FilterSection>
      </FilterSection>

      <FilterSection
        id="tuhono-monitoring"
        title="Monitoring"
        icon="monitoring"
        expanded={expanded.monitoring}
        onToggle={() => toggle('monitoring')}
        summary={monitoringSummary}
        className="sf-section--group"
      >
        <FilterSection
          id="tuhono-monitoring-domains"
          title="Domain"
          icon="monitoring"
          expanded={expanded.monitoringDomains}
          onToggle={() => toggle('monitoringDomains')}
          summary={getSelectionSummary(selectedMonitoring, monitoringOptions)}
          className="sf-section--nested"
        >
          <FilterCheckboxList
            options={monitoringOptions}
            selected={selectedMonitoring}
            onChange={onMonitoringChange}
            ariaLabel="Monitoring domains"
            emptyText="No monitoring domains available"
          />
        </FilterSection>

        <FilterSection
          id="tuhono-monitoring-groups"
          title="Monitoring Group"
          icon="layers"
          expanded={expanded.monitoringGroups}
          onToggle={() => toggle('monitoringGroups')}
          summary={getSelectionSummary(selectedMonitoringGroups, monitoringGroupOptions)}
          className="sf-section--nested"
        >
          <FilterCheckboxList
            options={monitoringGroupOptions}
            selected={selectedMonitoringGroups}
            onChange={onMonitoringGroupsChange}
            ariaLabel="Monitoring groups"
            emptyText="No monitoring groups available"
          />
        </FilterSection>

        <FilterSection
          id="tuhono-monitoring-locations"
          title="Monitoring Location"
          icon="station"
          expanded={expanded.monitoringLocations}
          onToggle={() => toggle('monitoringLocations')}
          summary={getSelectionSummary(selectedMonitoringLocations, monitoringLocationOptions)}
          className="sf-section--nested"
        >
          <FilterCheckboxList
            options={monitoringLocationOptions}
            selected={selectedMonitoringLocations}
            onChange={onMonitoringLocationsChange}
            ariaLabel="Monitoring locations"
            emptyText="No monitoring locations available"
          />
        </FilterSection>
      </FilterSection>
    </div>
  );
};

export default TuhonoFilterPanel;
